/**
 * Such-Faltung (Rudolf 05.10.2026 [RUDOLF], Story 1.32): Bei Suche und Text-Filtern sind Groß-/Kleinschreibung und
 * Umlaut-Schreibweisen egal – ä = a = ae, ö = o = oe, ü = u = ue, ß = ss (auch ẞ). „prazision“, „praezision“ und
 * „PRÄZISION“ finden „Präzision“; „präzision“ findet „Praezision“; „strasse“ findet „Straße“.
 *
 * EINE Regel für beide Seiten: `foldSearchText` (JavaScript) und `foldSearchSql` (SQL-Ausdruck für `reporting` und
 * die Trigramm-Indizes, Migration 0082) entstehen aus denselben Schritten `SEARCH_FOLD_STEPS`. Gefaltet werden immer
 * beide Seiten eines Vergleichs (Datensatz und Suchbegriff). Rein, ohne Datenbank.
 */

/** Großbuchstaben, die `lower()` bei Sortierfolge „C“ nicht faltet, und ihre Kleinbuchstaben. */
const UPPER_SPECIAL = 'ÄÖÜẞ';
const LOWER_SPECIAL = 'äöüß';

/**
 * Schritte nach dem Kleinschreiben, in dieser Reihenfolge: zuerst ß → ss, dann die Umschreibungen ae/oe/ue → a/o/u,
 * zuletzt die Umlaute selbst ä/ö/ü → a/o/u. (Die Umschreibungen vor den Umlauten: aus „ä“ entsteht nie ein neues „ae“.)
 */
export const SEARCH_FOLD_STEPS: readonly (readonly [from: string, to: string])[] = [
  ['ß', 'ss'],
  ['ae', 'a'],
  ['oe', 'o'],
  ['ue', 'u'],
  ['ä', 'a'],
  ['ö', 'o'],
  ['ü', 'u'],
];

/** Text für den Vergleich in der Suche (JavaScript-Seite derselben Regel wie `foldSearchSql`). */
export function foldSearchText(text: string): string {
  let out = [...text].map((ch) => {
    const i = UPPER_SPECIAL.indexOf(ch);
    return i >= 0 ? LOWER_SPECIAL[i]! : ch;
  }).join('');
  // Wie `lower()` in Postgres: nur ASCII sicher (Sortierfolge „C“); Umlaute deckt die Zeile darüber ab.
  out = out.replace(/[A-Z]/g, (c) => c.toLowerCase());
  for (const [from, to] of SEARCH_FOLD_STEPS) out = out.split(from).join(to);
  return out;
}

function sqlLiteral(text: string): string {
  return `'${text.replace(/'/g, "''")}'`;
}

/**
 * SQL-Ausdruck derselben Regel um einen Ausdruck `expr` (Spalte oder Parameter): `lower()`, dann Ä/Ö/Ü/ẞ klein, dann
 * die Schritte `SEARCH_FOLD_STEPS` – alles mit `replace()` (zeichenfolgen-genau in jeder Datenbank-Kodierung; `translate`
 * arbeitet bei Einzelbyte-Kodierung byteweise und zerlegte Umlaute). Nur Konstanten aus dem Code (keine Werte aus dem
 * Browser): `reporting` setzt ihn als Roh-SQL um die Spalte bzw. den Parameter; die Trigramm-Indizes (Migration 0082)
 * stehen auf genau diesem Ausdruck (Test: gleiche Faltung wie `foldSearchText` und Index-Gebrauch).
 */
export function foldSearchSql(expr: string): string {
  let out = `lower(${expr})`;
  const steps: (readonly [string, string])[] = [...[...UPPER_SPECIAL].map((ch, i) => [ch, LOWER_SPECIAL[i]!] as const), ...SEARCH_FOLD_STEPS];
  for (const [from, to] of steps) out = `replace(${out}, ${sqlLiteral(from)}, ${sqlLiteral(to)})`;
  return out;
}
